import { useState, useEffect } from 'react';
import { Container, Row, Col, Alert, Card, Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import { useLoyalty } from '../../../../apps/hooks/loyalty/useLoyalty';
import { TierManagementEnhanced } from '../components';

export default function TierManagementPage() {
  const {
    tierDefinitions,
    isLoadingTiers,
    isCreatingTier,
    isUpdatingTierDefinition,
    isDeletingTierDefinition,
    globalSettings,
    isLoadingGlobalSettings,
    isUpdatingGlobalSettings,
    updateGlobalSettings,
    error,
    createTier,
    updateTierDefinition,
    deleteTierDefinition,
    clearError,
  } = useLoyalty();

  const [maxWeeklyInteractionPoint, setMaxWeeklyInteractionPoint] = useState<string>('');
  const [interactionPointEntries, setInteractionPointEntries] = useState<{ key: string; value: string }[]>([]);
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    if (globalSettings?.maxWeeklyInteractionPoint !== undefined) {
      setMaxWeeklyInteractionPoint(String(globalSettings.maxWeeklyInteractionPoint));
    }
    if (globalSettings?.interactionPoints) {
      setInteractionPointEntries(
        Object.entries(globalSettings.interactionPoints).map(([key, value]) => ({ key, value: String(value) })),
      );
    }
  }, [globalSettings]);

  const handleSaveSettings = async () => {
    const weeklyValue = Number(maxWeeklyInteractionPoint);
    if (isNaN(weeklyValue) || weeklyValue < 0 || !globalSettings) return;

    const interactionPoints: Record<string, number> = {};
    for (const entry of interactionPointEntries) {
      const key = entry.key.trim();
      const value = Number(entry.value);
      if (key && !isNaN(value) && value >= 0) {
        interactionPoints[key] = value;
      }
    }

    try {
      await updateGlobalSettings({
        pointsExpiryDays: globalSettings.pointsExpiryDays,
        expiryNotificationDays: globalSettings.expiryNotificationDays,
        autoExpiryEnabled: globalSettings.autoExpiryEnabled,
        maxRedemptionPercentage: globalSettings.maxRedemptionPercentage,
        minRedemptionPoints: globalSettings.minRedemptionPoints,
        maxWeeklyInteractionPoint: weeklyValue,
        interactionPoints,
      });
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch {
      // error is handled by the hook
    }
  };

  const addInteractionPointEntry = () => {
    setInteractionPointEntries((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeInteractionPointEntry = (index: number) => {
    setInteractionPointEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updateInteractionPointEntry = (index: number, field: 'key' | 'value', val: string) => {
    setInteractionPointEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: val } : entry)));
  };

  return (
    <Container fluid className="p-4">
      <Row>
        <Col>
          <div className="mb-4">
            <h1>Tier Management</h1>
            <p className="text-muted">Configure and manage loyalty program tier definitions</p>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={clearError}>
              {error}
            </Alert>
          )}

          {settingsSaved && (
            <Alert variant="success" dismissible onClose={() => setSettingsSaved(false)}>
              Settings saved successfully.
            </Alert>
          )}

          <Card className="mb-4">
            <Card.Body>
              <Card.Title>Tier Overview</Card.Title>
              <p className="text-muted mb-0">
                Tiers define different levels in your loyalty program. Each tier has minimum point requirements, benefits,
                and display settings. Customers are automatically upgraded to higher tiers as they accumulate points.
              </p>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <strong>Global Settings</strong>
            </Card.Header>
            <Card.Body>
              {isLoadingGlobalSettings ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span className="text-muted">Loading settings...</span>
                </div>
              ) : (
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Weekly Interaction Point Limit</Form.Label>
                    <p className="text-muted small mb-2">
                      Set the maximum number of interaction points a customer can earn per week.
                    </p>
                    <InputGroup style={{ maxWidth: 360 }}>
                      <Form.Control
                        type="number"
                        min={0}
                        value={maxWeeklyInteractionPoint}
                        onChange={(e) => setMaxWeeklyInteractionPoint(e.target.value)}
                        placeholder="e.g. 100"
                      />
                    </InputGroup>
                    {maxWeeklyInteractionPoint !== '' && Number(maxWeeklyInteractionPoint) < 0 && (
                      <Form.Text className="text-danger">Value must be 0 or greater.</Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Interaction Points</Form.Label>
                    <p className="text-muted small mb-2">
                      Define how many points each interaction type awards.
                    </p>
                    <Row className="mb-2">
                      <Col xs={5}>
                        <small className="text-muted fw-semibold">Interaction Key</small>
                      </Col>
                      <Col xs={5}>
                        <small className="text-muted fw-semibold">Points</small>
                      </Col>
                    </Row>
                    {interactionPointEntries.map((entry, index) => (
                      <Row key={index} className="mb-2 align-items-center">
                        <Col xs={5}>
                          <Form.Control
                            type="text"
                            value={entry.key}
                            onChange={(e) => updateInteractionPointEntry(index, 'key', e.target.value)}
                            placeholder="e.g. purchase"
                            size="sm"
                          />
                        </Col>
                        <Col xs={5}>
                          <Form.Control
                            type="number"
                            value={entry.value}
                            onChange={(e) => updateInteractionPointEntry(index, 'value', e.target.value)}
                            placeholder="e.g. 10"
                            min={0}
                            size="sm"
                          />
                        </Col>
                        <Col xs={2}>
                          <Button variant="outline-danger" size="sm" onClick={() => removeInteractionPointEntry(index)}>
                            ✕
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button variant="outline-secondary" size="sm" className="mt-2" onClick={addInteractionPointEntry}>
                      + Add Entry
                    </Button>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isUpdatingGlobalSettings || maxWeeklyInteractionPoint === ''}
                  >
                    {isUpdatingGlobalSettings ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Saving...
                      </>
                    ) : (
                      'Save Settings'
                    )}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>

          <TierManagementEnhanced
            tiers={tierDefinitions || []}
            isLoading={isLoadingTiers}
            onCreateTier={createTier}
            onUpdateTier={updateTierDefinition}
            onDeleteTier={deleteTierDefinition}
            isCreating={isCreatingTier}
            isUpdating={isUpdatingTierDefinition}
            isDeleting={isDeletingTierDefinition}
          />
        </Col>
      </Row>
    </Container>
  );
}

